import { AlertTriangle, CheckCircle, X } from "lucide-react";
import React from "react";

interface FieldMappingConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    onEdit: () => void;
    subStep: any;
    fieldMapping: Record<string, string>;
    isRetry: boolean;
}

export const FieldMappingConfirmationModal:React.FC<FieldMappingConfirmationModalProps>= ({ isOpen, onClose, onConfirm, onEdit, subStep, fieldMapping, isRetry }) => {
  if (!isOpen || !subStep) return null;

  const requiredFields = subStep.fields.required || [];
  const optionalFields = subStep.fields.optional || [];

  const mappedRequiredFields = requiredFields.filter((field: any) => fieldMapping[field.fieldName]);
  const mappedOptionalFields = optionalFields.filter((field: any) => fieldMapping[field.fieldName]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>

        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isRetry ? 'Retry Import - ' : 'Confirm Import - '}{subStep.title}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Review your field mappings before {isRetry ? 'retrying' : 'starting'} the import
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Compact Stats Bar */}
          <div className="px-6 py-4 bg-white border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-200 rounded-full"></div>
                  <span className="font-medium text-gray-900">Required:</span>
                  <span className="font-semibold">
                    {mappedRequiredFields.length}/{requiredFields.length}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-200 rounded-full"></div>
                  <span className="font-medium text-gray-900">Optional:</span>
                  <span className="font-semibold text-gray-600">
                    {mappedOptionalFields.length}/{optionalFields.length}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">Total Mapped:</span>
                  <span className="font-semibold">
                    {mappedRequiredFields.length + mappedOptionalFields.length}
                  </span>
                </div>
              </div>
              {mappedRequiredFields.length < requiredFields.length && (
                <div className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm font-medium">Missing required fields</span>
                </div>
              )}
            </div>
          </div>

          {/* Compact Field Mappings Table */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-240px)]">
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Field Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      CSV Column
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {/* Required Fields */}
                  {requiredFields.map((field: any) => {
                    const csvColumn = fieldMapping[field.fieldName];
                    const isMapped = !!csvColumn;

                    return (
                      <tr key={field.fieldName} className={isMapped ? '' : 'bg-red-50'}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-red-200 rounded-full"></div>
                            <div>
                              <div className="font-medium text-gray-900 text-sm">{field.fieldName}</div>
                              <div className="text-xs text-gray-500 truncate max-w-xs" title={field.description}>
                                {field.description}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium">
                            Required
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {isMapped ? (
                            <span className="font-medium text-gray-900 bg-white px-2 py-1 rounded border text-sm">
                              {csvColumn}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic text-sm">Not mapped</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isMapped ? (
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-sm font-medium">Mapped</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-red-600">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="text-sm font-medium">Missing</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Optional Fields */}
                  {optionalFields.map((field: any) => {
                    const csvColumn = fieldMapping[field.fieldName];
                    const isMapped = !!csvColumn;

                    return (
                      <tr key={field.fieldName}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-yellow-200 rounded-full"></div>
                            <div>
                              <div className="font-medium text-gray-900 text-sm">{field.fieldName}</div>
                              <div className="text-xs text-gray-500 truncate max-w-xs" title={field.description}>
                                {field.description}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            Optional
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {isMapped ? (
                            <span className="font-medium text-gray-900 bg-white px-2 py-1 rounded border text-sm">
                              {csvColumn}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic text-sm">Skipped</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isMapped ? (
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              <span className="text-sm font-medium">Mapped</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-gray-400">
                              <span className="w-4 h-4 rounded-full border border-gray-300"></span>
                              <span className="text-sm">Skipped</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Validation Warning - Only show if missing required fields */}
            {mappedRequiredFields.length < requiredFields.length && (
              <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <span className="font-medium text-red-800">Action Required</span>
                </div>
                <p className="text-red-700 text-sm mt-1">
                  {requiredFields.length - mappedRequiredFields.length} required field{requiredFields.length - mappedRequiredFields.length > 1 ? 's' : ''} must be mapped before importing.
                </p>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="flex justify-between items-center gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-600">
              {mappedRequiredFields.length + mappedOptionalFields.length} of {requiredFields.length + optionalFields.length} fields mapped
            </div>
            <div className="flex gap-3">
              <button
                onClick={onEdit}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Edit Mapping
              </button>
              <button
                onClick={onConfirm}
                disabled={mappedRequiredFields.length < requiredFields.length}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${mappedRequiredFields.length < requiredFields.length
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
              >
                {isRetry ? 'Retry Import' : 'Confirm & Import'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};